import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Trash2, Edit2, ChevronDown, ChevronRight, Unlock, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TimesheetPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Check if coming from dashboard unsubmitted warning
  const isUnsubmittedFlow = searchParams.get('unsubmitted') === 'true' && user?.role === 'poc';
  
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedPOC, setSelectedPOC] = useState(null);
  const [pocList, setPocList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTodayIST());
  const [isRangeMode, setIsRangeMode] = useState(isUnsubmittedFlow); // Start in range mode if unsubmitted flow
  const [dateRange, setDateRange] = useState({ from: getTodayIST(), to: getTodayIST() });
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: getTodayIST(),
    start_time: '',
    end_time: '',
    activity_type: '',
    firm: '',
    notes: ''
  });
  
  // Flag to track if unsubmitted date range has been set
  const hasSetUnsubmittedRange = useRef(false);
  
  // Convert to follow-up states
  const [convertToFollowUp, setConvertToFollowUp] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpFirmSearch, setFollowUpFirmSearch] = useState('');
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpOutcome, setFollowUpOutcome] = useState('interested');
  const [followUpNextDate, setFollowUpNextDate] = useState('');
  const [activeFirms, setActiveFirms] = useState([]);
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [pendingTimesheetSave, setPendingTimesheetSave] = useState(null);
  const [followUpError, setFollowUpError] = useState('');
  const [followUpSuccess, setFollowUpSuccess] = useState(false);
  
  // Admin unlock/audit states
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockingDate, setUnlockingDate] = useState(null);
  const [unlockingUserEmail, setUnlockingUserEmail] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  
  // Submit confirmation modal state
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [submitDateInfo, setSubmitDateInfo] = useState({ date: '', count: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Collapsible day view for admin range mode
  const [expandedDays, setExpandedDays] = useState({});
  
  // Expanded notes state (accordion behavior)
  const [expandedNote, setExpandedNote] = useState(null); // Format: "entryId"
  
  // Unsubmitted dates modal for POC
  const [showUnsubmittedModal, setShowUnsubmittedModal] = useState(false);
  const [expandedUnsubmittedDays, setExpandedUnsubmittedDays] = useState({});

  // Filter states for Admin/Super Admin
  const [filterActivity, setFilterActivity] = useState('');
  const [filterType, setFilterType] = useState('');

  // Get today's date in IST
  function getTodayIST() {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().split('T')[0];
  }

  // Format duration from decimal hours to "Xh Ym" format
  function formatDuration(decimalHours) {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  }

  // Calculate duration in "Xh Ym" format
  const calculateDurationFormatted = (start, end) => {
    if (!start || !end) return '0h';
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const totalMinutes = endMinutes - startMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  // Check if selected date's entries are already submitted (for POC)
  const isSelectedDateSubmitted = () => {
    const currentDate = isRangeMode ? dateRange.from : selectedDate;
    return entries.some(e => e.date === currentDate && e.submitted);
  };

  // Check if POC is viewing today (for Add Entry button)
  const isViewingToday = () => {
    const today = getTodayIST();
    if (isRangeMode) {
      // In range mode, check if 'from' date is today
      return dateRange.from === today;
    } else {
      // In single mode, check if selected date is today
      return selectedDate === today;
    }
  };

  // Check if Add Entry button should be enabled for POC
  const canAddEntry = () => {
    if (user.role !== 'poc') return false;
    // POCs can add entries if the selected date is NOT submitted (even past dates)
    return !isSelectedDateSubmitted();
  };
  
  // Check if Submit button should be visible for POC
  const canSubmitDate = () => {
    if (user.role !== 'poc') return false;
    const currentDate = isRangeMode ? dateRange.from : selectedDate;
    // Can submit if there are unsubmitted entries for the selected date
    return entries.some(e => e.date === currentDate && !e.submitted);
  };

  // Fetch activity categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/timesheet/categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch active trial firms (for convert to follow-up)
  useEffect(() => {
    if (user.role === 'poc') {
      const fetchActiveFirms = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/firms`, {
            withCredentials: true
          });
          setActiveFirms(response.data.filter(f => f.is_active));
        } catch (error) {
          console.error('Error fetching firms:', error);
        }
      };
      fetchActiveFirms();
    }
  }, [user.role]);

  // Fetch POC list (for admins)
  useEffect(() => {
    if (user.role === 'admin' || user.role === 'super_admin') {
      const fetchPOCs = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/timesheet/pocs`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          setPocList(response.data.pocs);
          if (response.data.pocs.length > 0) {
            setSelectedPOC(response.data.pocs[0].email);
          }
        } catch (error) {
          console.error('Error fetching POCs:', error);
        }
      };
      fetchPOCs();
    }
  }, [user.role]);

  // Fetch timesheet entries
  useEffect(() => {
    fetchEntries();
  }, [selectedDate, dateRange, isRangeMode, selectedPOC]);

  // Handle unsubmitted query param from Dashboard - set date range after entries load
  useEffect(() => {
    if (
      isUnsubmittedFlow && 
      entries.length > 0 && 
      !hasSetUnsubmittedRange.current
    ) {
      // Get earliest unsubmitted date
      const unsubmittedDates = entries
        .filter(e => !e.submitted)
        .map(e => e.date)
        .sort();
      
      if (unsubmittedDates.length > 0) {
        hasSetUnsubmittedRange.current = true;
        setDateRange({
          from: unsubmittedDates[0],
          to: getTodayIST()
        });
      }
    }
  }, [isUnsubmittedFlow, entries]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      let params = {};
      
      // For unsubmitted flow on first load, fetch ALL entries to find unsubmitted dates
      if (isUnsubmittedFlow && !hasSetUnsubmittedRange.current) {
        // Don't add date filters - fetch all entries
      } else if (isRangeMode) {
        params.from_date = dateRange.from;
        params.to_date = dateRange.to;
      } else {
        params.date = selectedDate;
      }

      if ((user.role === 'admin' || user.role === 'super_admin') && selectedPOC) {
        params.user_email = selectedPOC;
      }

      const response = await axios.get(`${API_URL}/api/timesheet`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEntries(response.data.entries);
      
      // Collapse all days initially for admin range view
      if (isRangeMode && (user.role === 'admin' || user.role === 'super_admin')) {
        const uniqueDates = [...new Set(response.data.entries.map(e => e.date))];
        const initialExpanded = {};
        uniqueDates.forEach(date => {
          initialExpanded[date] = false; // Collapsed by default
        });
        setExpandedDays(initialExpanded);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate duration from start and end time
  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return ((endMinutes - startMinutes) / 60).toFixed(2);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If convert to follow-up is checked, save timesheet first then open modal
    if (convertToFollowUp) {
      try {
        let entryResponse;
        
        if (editingEntry) {
          // Update existing entry
          await axios.patch(`${API_URL}/api/timesheet/${editingEntry}`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          // Create new entry
          entryResponse = await axios.post(`${API_URL}/api/timesheet`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        }
        
        // Store pending timesheet save data
        setPendingTimesheetSave({ success: true, data: entryResponse?.data });
        
        // Pre-fill follow-up modal
        setFollowUpNote(formData.notes);
        setFollowUpFirmSearch('');
        setSelectedFirm(null);
        setFollowUpOutcome('interested');
        setFollowUpNextDate('');
        
        // Open follow-up modal
        setShowFollowUpModal(true);
        setShowEntryForm(false);
      } catch (error) {
        alert(error.response?.data?.detail || 'Error saving entry');
      }
    } else {
      // Normal save without follow-up
      try {
        if (editingEntry) {
          // Update existing entry
          await axios.patch(`${API_URL}/api/timesheet/${editingEntry}`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } else {
          // Create new entry
          await axios.post(`${API_URL}/api/timesheet`, formData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        }
        
        // Reset form and refresh
        resetForm();
        fetchEntries();
      } catch (error) {
        alert(error.response?.data?.detail || 'Error saving entry');
      }
    }
  };

  // Handle follow-up submission
  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFirm) {
      setFollowUpError('Please select a trial firm');
      return;
    }
    
    if (followUpNote.trim().length < 10) {
      setFollowUpError('Follow-up note must be at least 10 characters long');
      return;
    }
    
    setSavingFollowUp(true);
    setFollowUpError('');
    
    try {
      await axios.post(
        `${API_URL}/api/firms/${encodeURIComponent(selectedFirm.site_url)}/follow-ups`,
        {
          note: followUpNote,
          outcome: followUpOutcome,
          next_follow_up_date: followUpNextDate || null
        },
        { withCredentials: true }
      );
      
      setShowFollowUpModal(false);
      setFollowUpSuccess(true);
      setTimeout(() => setFollowUpSuccess(false), 3000);
      
      resetForm();
      fetchEntries();
    } catch (error) {
      setFollowUpError(error.response?.data?.detail || 'Error creating follow-up. Please try again.');
    } finally {
      setSavingFollowUp(false);
    }
  };

  // Handle follow-up modal cancel
  const handleFollowUpCancel = () => {
    setShowFollowUpModal(false);
    // Timesheet was already saved, just close modal
    resetForm();
    fetchEntries();
  };

  const resetForm = () => {
    setShowEntryForm(false);
    setEditingEntry(null);
    setConvertToFollowUp(false);
    setPendingTimesheetSave(null);
    setFollowUpError('');
    setFormData({
      date: getTodayIST(),
      start_time: '',
      end_time: '',
      activity_type: '',
      firm: '',
      notes: ''
    });
  };

  // Handle delete
  const handleDelete = (entryId) => {
    setDeletingEntryId(entryId);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/timesheet/${deletingEntryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowDeleteModal(false);
      setDeletingEntryId(null);
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting entry');
    } finally {
      setDeleting(false);
    }
  };

  // Handle edit
  const handleEdit = (entry) => {
    setEditingEntry(entry.entry_id);
    setFormData({
      date: entry.date || '',
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      activity_type: entry.activity_type || '',
      firm: entry.firm || '',
      notes: entry.notes || ''
    });
    setShowEntryForm(true);
  };

  // Handle submit timesheet - open confirmation modal
  const handleSubmitTimesheet = () => {
    const dateToSubmit = isRangeMode ? dateRange.from : selectedDate;
    const unsubmittedEntries = entries.filter(e => !e.submitted && e.date === dateToSubmit);
    
    if (unsubmittedEntries.length === 0) {
      setSuccessMessage('No unsubmitted entries to submit for this date');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return;
    }

    setSubmitDateInfo({ date: dateToSubmit, count: unsubmittedEntries.length });
    setShowSubmitConfirmModal(true);
  };
  
  // Handle submit all unsubmitted (for range mode)
  const handleSubmitAllUnsubmitted = () => {
    const unsubmittedByDate = {};
    entries.filter(e => !e.submitted).forEach(e => {
      if (!unsubmittedByDate[e.date]) {
        unsubmittedByDate[e.date] = 0;
      }
      unsubmittedByDate[e.date]++;
    });
    
    const dates = Object.keys(unsubmittedByDate).sort();
    if (dates.length === 0) {
      setSuccessMessage('No unsubmitted entries found');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return;
    }
    
    const totalCount = Object.values(unsubmittedByDate).reduce((sum, count) => sum + count, 0);
    setSubmitDateInfo({ 
      date: dates.length === 1 ? dates[0] : `${dates.length} dates`, 
      count: totalCount,
      multipleDates: dates 
    });
    setShowSubmitConfirmModal(true);
  };
  
  // Confirm and execute submit
  const confirmSubmitTimesheet = async () => {
    setSubmitting(true);
    try {
      if (submitDateInfo.multipleDates) {
        // Submit multiple dates
        for (const date of submitDateInfo.multipleDates) {
          await axios.post(`${API_URL}/api/timesheet/submit`, 
            { date },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
        }
        setSuccessMessage(`Submitted ${submitDateInfo.multipleDates.length} dates successfully!`);
      } else {
        // Submit single date
        await axios.post(`${API_URL}/api/timesheet/submit`, 
          { date: submitDateInfo.date },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        setSuccessMessage('Timesheet submitted successfully!');
      }
      
      setShowSubmitConfirmModal(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error submitting timesheet');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle unlock day (Admin/Super Admin)
  const handleUnlockDay = async () => {
    if (!unlockingDate) return;
    
    setUnlocking(true);
    try {
      await axios.post(
        `${API_URL}/api/timesheet/unlock`,
        { date: unlockingDate, user_email: unlockingUserEmail || selectedPOC },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setSuccessMessage(`Day ${unlockingDate} unlocked successfully`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      setShowUnlockModal(false);
      setUnlockingDate(null);
      setUnlockingUserEmail(null);
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error unlocking day');
    } finally {
      setUnlocking(false);
    }
  };

  const duration = formData.start_time && formData.end_time 
    ? calculateDurationFormatted(formData.start_time, formData.end_time)
    : '0h';

  const unsubmittedCount = entries.filter(e => !e.submitted).length;
  const unsubmittedDatesCount = [...new Set(entries.filter(e => !e.submitted).map(e => e.date))].length;
  const totalHours = entries.reduce((sum, e) => sum + e.duration_hours, 0).toFixed(2);
  const rgaHours = entries.filter(e => e.rga_nra === 'RGA').reduce((sum, e) => sum + e.duration_hours, 0).toFixed(2);
  const nraHours = entries.filter(e => e.rga_nra === 'NRA').reduce((sum, e) => sum + e.duration_hours, 0).toFixed(2);

  // Format total hours for display
  const totalHoursFormatted = formatDuration(parseFloat(totalHours));
  const rgaHoursFormatted = formatDuration(parseFloat(rgaHours));
  const nraHoursFormatted = formatDuration(parseFloat(nraHours));

  // Filter firms based on search
  const filteredFirms = activeFirms.filter(f =>
    f.firm_name.toLowerCase().includes(followUpFirmSearch.toLowerCase()) ||
    f.site_url.toLowerCase().includes(followUpFirmSearch.toLowerCase())
  );

  // Group entries by date for admin range view
  const groupedByDate = {};
  entries.forEach(entry => {
    if (!groupedByDate[entry.date]) {
      groupedByDate[entry.date] = [];
    }
    groupedByDate[entry.date].push(entry);
  });
  const sortedDates = Object.keys(groupedByDate).sort().reverse(); // Most recent first

  const toggleDayExpansion = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };
  
  const toggleNoteExpansion = (entryId) => {
    setExpandedNote(prev => prev === entryId ? null : entryId);
  };
  
  const toggleUnsubmittedDayExpansion = (date) => {
    setExpandedUnsubmittedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };
  
  // Get unsubmitted and unlocked entries grouped by date
  const getUnsubmittedEntriesByDate = () => {
    const unsubmittedEntries = entries.filter(e => !e.submitted);
    const grouped = {};
    unsubmittedEntries.forEach(entry => {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      grouped[entry.date].push(entry);
    });
    return grouped;
  };
  
  // Submit entries for a specific date
  const handleSubmitSpecificDate = async (date) => {
    try {
      await axios.post(`${API_URL}/api/timesheet/submit`, 
        { date },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSuccessMessage(`Submitted entries for ${date} successfully!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error submitting entries');
    }
  };
  
  // Submit all unsubmitted entries
  const handleSubmitAllUnsubmittedFromModal = async () => {
    const unsubmittedByDate = getUnsubmittedEntriesByDate();
    const dates = Object.keys(unsubmittedByDate).sort();
    
    if (dates.length === 0) {
      setSuccessMessage('No unsubmitted entries found');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      return;
    }
    
    try {
      for (const date of dates) {
        await axios.post(`${API_URL}/api/timesheet/submit`, 
          { date },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      }
      setSuccessMessage(`Submitted all entries across ${dates.length} dates successfully!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      setShowUnsubmittedModal(false);
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error submitting entries');
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto">
        {/* Success Message Toast */}
        {showSuccessMessage && (
          <div className="fixed top-20 right-6 z-50 animate-slideIn">
            <div className="bg-green-50 border-l-4 border-green-500 p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-sm font-medium text-green-900">{successMessage}</p>
                <button
                  onClick={() => setShowSuccessMessage(false)}
                  className="ml-4 text-green-500 hover:text-green-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Follow-up Success Toast */}
        {followUpSuccess && (
          <div className="fixed top-20 right-6 z-50 animate-slideIn">
            <div className="bg-green-50 border-l-4 border-green-500 p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-sm font-medium text-green-900">Follow-up created successfully!</p>
                <button
                  onClick={() => setFollowUpSuccess(false)}
                  className="ml-4 text-green-500 hover:text-green-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#09090B] mb-2">Timesheet</h1>
            <p className="text-sm text-[#71717A]">
              {user.role === 'poc' ? 'Track your daily activities' : 'View POC activities'}
            </p>
          </div>
          
          <div className="flex gap-3">
            {user.role === 'poc' && unsubmittedDatesCount > 0 && (
              <button
                onClick={() => {
                  setShowUnsubmittedModal(true);
                  // Auto-expand all unsubmitted dates
                  const unsubmittedByDate = getUnsubmittedEntriesByDate();
                  const initialExpanded = {};
                  Object.keys(unsubmittedByDate).forEach(date => {
                    initialExpanded[date] = true;
                  });
                  setExpandedUnsubmittedDays(initialExpanded);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#DC2626] text-white hover:bg-[#B91C1C] transition-colors border border-[#B91C1C]"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {unsubmittedDatesCount} Pending Date{unsubmittedDatesCount > 1 ? 's' : ''}
              </button>
            )}
            {user.role === 'poc' && (
              <button
                onClick={() => {
                  setShowEntryForm(true);
                  setEditingEntry(null);
                  setConvertToFollowUp(false);
                  const currentDate = isRangeMode ? dateRange.from : selectedDate;
                  setFormData({
                    date: currentDate,
                    start_time: '',
                    end_time: '',
                    activity_category: '',
                    client_lead_name: '',
                    notes: ''
                  });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0C9168] text-white hover:bg-[#0a7a58] transition-colors"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add Entry
              </button>
            )}
          </div>
        </div>

        {/* POC Selection Bar with Date Filters (Admin only) */}
        {(user.role === 'admin' || user.role === 'super_admin') && (
          <div className="bg-white border border-[#E4E4E7] p-4 mb-6">
            {/* POC Selection */}
            <div className="mb-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#71717A] mb-3">
                Select POC
              </div>
              <div className="flex gap-2 flex-wrap">
                {pocList.map(poc => (
                  <button
                    key={poc.email}
                    onClick={() => setSelectedPOC(poc.email)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      selectedPOC === poc.email
                        ? 'bg-[#0C9168] text-white'
                        : 'bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]'
                    }`}
                  >
                    {poc.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters and Date Selection Row */}
            <div className="flex items-end justify-between gap-4">
              {/* Left: Activity and Type Filters */}
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#71717A] mb-2">
                    Activity
                  </label>
                  <select
                    value={filterActivity}
                    onChange={(e) => {
                      setFilterActivity(e.target.value);
                      if (e.target.value) {
                        setFilterType(''); // Reset Type when Activity is selected
                      }
                    }}
                    disabled={!!filterType}
                    className={`px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm ${
                      filterType ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">All Activities</option>
                    {categories.map(cat => (
                      <option key={cat.name} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#71717A] mb-2">
                    Type
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      if (e.target.value) {
                        setFilterActivity(''); // Reset Activity when Type is selected
                      }
                    }}
                    disabled={!!filterActivity}
                    className={`px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm ${
                      filterActivity ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">All Types</option>
                    <option value="RGA">RGA</option>
                    <option value="NRA">NRA</option>
                  </select>
                </div>
                
                {/* Clear Filters Button */}
                {(filterActivity || filterType) && (
                  <button
                    onClick={() => {
                      setFilterActivity('');
                      setFilterType('');
                    }}
                    className="px-3 py-2 bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7] transition-colors flex items-center gap-2 text-sm font-medium"
                    title="Clear filters"
                  >
                    <X className="w-4 h-4" strokeWidth={2} />
                    Clear
                  </button>
                )}
              </div>

              {/* Right: Date Selection */}
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">From Date</label>
                  <input
                    type="date"
                    value={isRangeMode ? dateRange.from : selectedDate}
                    max={getTodayIST()}
                    onChange={(e) => {
                      if (isRangeMode) {
                        setDateRange({ ...dateRange, from: e.target.value });
                      } else {
                        setSelectedDate(e.target.value);
                      }
                    }}
                    className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  />
                </div>

                {isRangeMode && (
                  <div>
                    <label className="block text-xs text-[#71717A] mb-1">To Date</label>
                    <input
                      type="date"
                      value={dateRange.to}
                      max={getTodayIST()}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                      className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                  </div>
                )}

                {/* Range Mode Toggle */}
                <div className="flex gap-1 border border-[#E4E4E7]">
                  <button
                    onClick={() => setIsRangeMode(false)}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                      !isRangeMode
                        ? 'bg-[#0C9168] text-white'
                        : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                    }`}
                  >
                    Single
                  </button>
                  <button
                    onClick={() => setIsRangeMode(true)}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                      isRangeMode
                        ? 'bg-[#0C9168] text-white'
                        : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                    }`}
                  >
                    Range
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date Filters (POC only) */}
        {user.role === 'poc' && (
          <div className="bg-white border border-[#E4E4E7] p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#71717A] mb-1">
                  Date Selection
                </div>
                <div className="text-sm text-[#52525B]">
                  Total: <span className="font-bold text-[#09090B]">{totalHoursFormatted}</span>
                  <span className="mx-2">•</span>
                  RGA: <span className="font-bold text-[#0C9168]">{rgaHoursFormatted}</span>
                  <span className="mx-2">•</span>
                  NRA: <span className="font-bold text-[#52525B]">{nraHoursFormatted}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">From Date</label>
                  <input
                    type="date"
                    value={isRangeMode ? dateRange.from : selectedDate}
                    max={getTodayIST()}
                    onChange={(e) => {
                      if (isRangeMode) {
                        setDateRange({ ...dateRange, from: e.target.value });
                      } else {
                        setSelectedDate(e.target.value);
                      }
                    }}
                    className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                  />
                </div>

                {isRangeMode && (
                  <div>
                    <label className="block text-xs text-[#71717A] mb-1">To Date</label>
                    <input
                      type="date"
                      value={dateRange.to}
                      max={getTodayIST()}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                      className="px-3 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                  </div>
                )}

                {/* Range Mode Toggle */}
                <div className="flex gap-1 border border-[#E4E4E7] self-end">
                  <button
                    onClick={() => setIsRangeMode(false)}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                      !isRangeMode
                        ? 'bg-[#0C9168] text-white'
                        : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                    }`}
                  >
                    Single
                  </button>
                  <button
                    onClick={() => setIsRangeMode(true)}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                      isRangeMode
                        ? 'bg-[#0C9168] text-white'
                        : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
                    }`}
                  >
                    Range
                  </button>
                </div>

                {/* Unsubmitted Dates Button */}
                {unsubmittedDatesCount > 0 && (
                  <button
                    onClick={() => {
                      const unsubmittedDates = [...new Set(entries.filter(e => !e.submitted).map(e => e.date))].sort();
                      if (unsubmittedDates.length > 0) {
                        setIsRangeMode(true);
                        setDateRange({
                          from: unsubmittedDates[0],
                          to: getTodayIST()
                        });
                      }
                    }}
                    className="self-end px-4 py-2 bg-[#DC2626] text-white text-xs font-medium hover:bg-[#B91C1C] transition-colors flex items-center gap-1 border border-[#B91C1C]"
                  >
                    <span className="font-bold">{unsubmittedDatesCount}</span> Unsubmitted Date{unsubmittedDatesCount > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Entry Form Modal */}
        {showEntryForm && user.role === 'poc' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-[#09090B] mb-4">
                  {editingEntry ? 'Edit Entry' : 'Add Entry'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Date (editable - POC can choose any date) */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                  </div>

                  {/* Time Fields */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        End Time *
                      </label>
                      <input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Duration
                      </label>
                      <div className="px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#09090B] text-sm font-bold">
                        {duration}
                      </div>
                    </div>
                  </div>

                  {/* Activity Category */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Activity Category *
                    </label>
                    <select
                      value={formData.activity_type}
                      onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                      required
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    >
                      <option value="">Select activity...</option>
                      <optgroup label="RGA (Revenue Generating)">
                        {categories.filter(c => c.type === 'RGA').map(cat => (
                          <option key={cat.name} value={cat.name}>{cat.name}</option>
                        ))}
                      </optgroup>
                      <optgroup label="NRA (Non-Revenue)">
                        {categories.filter(c => c.type === 'NRA').map(cat => (
                          <option key={cat.name} value={cat.name}>{cat.name}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* Client/Lead Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Client / Lead Name
                    </label>
                    <input
                      type="text"
                      value={formData.firm}
                      onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      placeholder="Enter client or lead name (optional)"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Notes *
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm resize-none"
                      placeholder="Describe your activity..."
                    />
                  </div>

                  {/* Convert to Follow-up Checkbox */}
                  <div className="pt-4 border-t border-[#E4E4E7]">
                    <label className="flex items-center gap-2 text-sm text-[#09090B]">
                      <input
                        type="checkbox"
                        checked={convertToFollowUp}
                        onChange={(e) => setConvertToFollowUp(e.target.checked)}
                        className="rounded border-[#E4E4E7]"
                      />
                      <span className="font-medium">Convert notes to trial firm follow-up</span>
                    </label>
                    {convertToFollowUp && (
                      <p className="text-xs text-[#71717A] mt-2 ml-6">
                        After saving this entry, you&apos;ll be prompted to select a trial firm and create a follow-up note.
                      </p>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors"
                    >
                      {editingEntry ? 'Update Entry' : 'Add Entry'}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Modal */}
        {showFollowUpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-[#09090B] mb-2">Create Trial Firm Follow-up</h2>
                <p className="text-sm text-[#71717A] mb-4">
                  Your timesheet entry has been saved. Now create a follow-up note for a trial firm.
                </p>
                
                <form onSubmit={handleFollowUpSubmit} className="space-y-4">
                  {/* Firm Search/Select */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Select Trial Firm *
                    </label>
                    <input
                      type="text"
                      value={followUpFirmSearch}
                      onChange={(e) => setFollowUpFirmSearch(e.target.value)}
                      placeholder="Search by firm name or URL..."
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                    />
                    {followUpFirmSearch && (
                      <div className="mt-2 max-h-48 overflow-y-auto border border-[#E4E4E7] bg-white">
                        {filteredFirms.length === 0 ? (
                          <div className="p-3 text-sm text-[#71717A]">No firms found</div>
                        ) : (
                          filteredFirms.map(firm => (
                            <button
                              key={firm.site_url}
                              type="button"
                              onClick={() => {
                                setSelectedFirm(firm);
                                setFollowUpFirmSearch(`${firm.firm_name} (${firm.site_url})`);
                              }}
                              className={`w-full text-left p-3 text-sm hover:bg-[#F4F4F5] transition-colors ${
                                selectedFirm?.site_url === firm.site_url ? 'bg-[#D1FAE5]' : ''
                              }`}
                            >
                              <div className="font-medium text-[#09090B]">{firm.firm_name}</div>
                              <div className="text-xs text-[#71717A]">{firm.site_url}</div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Note */}
                  <div>
                    <label className="block text-sm font-medium text-[#09090B] mb-1">
                      Follow-up Note *
                    </label>
                    <textarea
                      value={followUpNote}
                      onChange={(e) => {
                        setFollowUpNote(e.target.value);
                        setFollowUpError('');
                      }}
                      required
                      rows={4}
                      className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm resize-none"
                      placeholder="Edit or add to your note..."
                    />
                  </div>
                  
                  {followUpError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3">
                      <p className="text-sm text-red-900">{followUpError}</p>
                    </div>
                  )}

                  {/* Outcome */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Outcome *
                      </label>
                      <select
                        value={followUpOutcome}
                        onChange={(e) => setFollowUpOutcome(e.target.value)}
                        required
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      >
                        <option value="interested">Interested</option>
                        <option value="cold">Cold</option>
                        <option value="purchased">Purchased</option>
                        <option value="no_response">No Response</option>
                        <option value="extended">Extended</option>
                        <option value="deactivated">Deactivated</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#09090B] mb-1">
                        Next Follow-up Date
                      </label>
                      <input
                        type="date"
                        value={followUpNextDate}
                        onChange={(e) => setFollowUpNextDate(e.target.value)}
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-white text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={savingFollowUp}
                      className="flex-1 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors disabled:opacity-50"
                    >
                      {savingFollowUp ? 'Creating...' : 'Create Follow-up'}
                    </button>
                    <button
                      type="button"
                      onClick={handleFollowUpCancel}
                      className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                    >
                      Skip Follow-up
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Unlock Confirmation Modal */}
        {showUnlockModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-md w-full p-6">
              <h2 className="text-lg font-bold text-[#09090B] mb-4">Unlock Submitted Timesheet</h2>
              <p className="text-sm text-[#52525B] mb-6">
                Are you sure you want to unlock all submitted entries for <strong>{unlockingDate}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleUnlockDay}
                  disabled={unlocking}
                  className="flex-1 px-4 py-2 bg-[#DC2626] text-white text-sm font-medium hover:bg-[#b91c1c] transition-colors disabled:opacity-50"
                >
                  {unlocking ? 'Unlocking...' : 'Unlock'}
                </button>
                <button
                  onClick={() => {
                    setShowUnlockModal(false);
                    setUnlockingDate(null);
                  }}
                  disabled={unlocking}
                  className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-md w-full p-6">
              <h2 className="text-lg font-bold text-[#09090B] mb-4">Delete Entry</h2>
              <p className="text-sm text-[#52525B] mb-6">
                Are you sure you want to delete this timesheet entry? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-[#DC2626] text-white text-sm font-medium hover:bg-[#b91c1c] transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingEntryId(null);
                  }}
                  disabled={deleting}
                  className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unsubmitted Dates Modal for POC */}
        {showUnsubmittedModal && user.role === 'poc' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-[#E4E4E7] p-6 z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[#09090B]">Pending Entries</h2>
                    <p className="text-sm text-[#71717A] mt-1">
                      Unsubmitted and unlocked entries that need your attention
                    </p>
                  </div>
                  <button
                    onClick={() => setShowUnsubmittedModal(false)}
                    className="text-[#71717A] hover:text-[#09090B] transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {Object.keys(getUnsubmittedEntriesByDate()).length === 0 ? (
                  <div className="text-center py-12 text-[#71717A]">
                    <svg className="w-16 h-16 mx-auto mb-4 text-[#D4D4D8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-lg font-medium">All caught up!</p>
                    <p className="text-sm mt-1">You have no pending entries to submit.</p>
                  </div>
                ) : (
                  <>
                    {Object.keys(getUnsubmittedEntriesByDate()).sort().map(date => {
                      const dayEntries = getUnsubmittedEntriesByDate()[date];
                      const isExpanded = expandedUnsubmittedDays[date];
                      const dayTotalHours = dayEntries.reduce((sum, e) => sum + e.duration_hours, 0);
                      
                      return (
                        <div key={date} className="border border-[#E4E4E7] mb-4 last:mb-0">
                          <div className="flex items-center justify-between bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors">
                            <button
                              onClick={() => toggleUnsubmittedDayExpansion(date)}
                              className="flex-1 p-4 flex items-center gap-3 text-left"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-[#52525B]" strokeWidth={2} />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-[#52525B]" strokeWidth={2} />
                              )}
                              <div className="flex items-center gap-4 flex-1">
                                <span className="text-base font-bold text-[#09090B]">
                                  {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                <span className="text-sm text-[#71717A]">
                                  {dayEntries.length} {dayEntries.length === 1 ? 'entry' : 'entries'}
                                </span>
                                <span className="text-sm font-bold text-[#09090B]">
                                  {formatDuration(dayTotalHours)} total
                                </span>
                              </div>
                            </button>
                            
                            <button
                              onClick={() => handleSubmitSpecificDate(date)}
                              className="mr-4 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors"
                            >
                              Submit Day
                            </button>
                          </div>
                          
                          {isExpanded && (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-[#F4F4F5] border-t border-b border-[#E4E4E7]">
                                  <tr>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Time</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Duration</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Activity</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Type</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Client/Lead</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Notes</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dayEntries.map((entry, idx) => (
                                    <tr key={idx} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA] last:border-b-0">
                                      <td className="p-3 text-sm text-[#52525B] font-mono">
                                        {entry.start_time} - {entry.end_time}
                                      </td>
                                      <td className="p-3 text-sm text-[#09090B] font-bold">{formatDuration(entry.duration_hours)}</td>
                                      <td className="p-3 text-sm text-[#52525B]">{entry.activity_type || '—'}</td>
                                      <td className="p-3">
                                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                                          entry.rga_nra === 'RGA' 
                                            ? 'bg-green-100 text-green-900 border border-green-900'
                                            : 'bg-gray-100 text-gray-900 border border-gray-900'
                                        }`}>
                                          {entry.rga_nra}
                                        </span>
                                      </td>
                                      <td className="p-3 text-sm text-[#52525B]">{entry.firm || '—'}</td>
                                      <td className="p-3 text-sm text-[#52525B] max-w-xs truncate">{entry.notes}</td>
                                      <td className="p-3">
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => {
                                              handleEdit(entry);
                                              setShowUnsubmittedModal(false);
                                            }}
                                            className="p-1 text-[#0C9168] hover:bg-[#F4F4F5] transition-colors"
                                            title="Edit"
                                          >
                                            <Edit2 className="w-4 h-4" strokeWidth={2} />
                                          </button>
                                          <button
                                            onClick={() => {
                                              handleDelete(entry.entry_id);
                                              setShowUnsubmittedModal(false);
                                            }}
                                            className="p-1 text-[#DC2626] hover:bg-[#F4F4F5] transition-colors"
                                            title="Delete"
                                          >
                                            <Trash2 className="w-4 h-4" strokeWidth={2} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Submit All Button */}
                    {Object.keys(getUnsubmittedEntriesByDate()).length > 0 && (
                      <div className="mt-6 pt-6 border-t border-[#E4E4E7] flex justify-end">
                        <button
                          onClick={handleSubmitAllUnsubmittedFromModal}
                          className="px-6 py-3 bg-[#0C9168] text-white text-base font-medium hover:bg-[#0a7a58] transition-colors"
                        >
                          Submit All {Object.keys(getUnsubmittedEntriesByDate()).length} Date{Object.keys(getUnsubmittedEntriesByDate()).length > 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Submit Confirmation Modal */}
        {showSubmitConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white max-w-md w-full p-6">
              <h2 className="text-lg font-bold text-[#09090B] mb-4">Submit Timesheet</h2>
              <p className="text-sm text-[#52525B] mb-6">
                {submitDateInfo.multipleDates ? (
                  <>
                    Submit <strong>{submitDateInfo.count}</strong> {submitDateInfo.count === 1 ? 'entry' : 'entries'} across <strong>{submitDateInfo.multipleDates.length}</strong> {submitDateInfo.multipleDates.length === 1 ? 'date' : 'dates'}?
                    <br />
                    <span className="text-xs text-[#71717A] mt-1 block">Dates: {submitDateInfo.multipleDates.join(', ')}</span>
                  </>
                ) : (
                  <>
                    Submit <strong>{submitDateInfo.count}</strong> {submitDateInfo.count === 1 ? 'entry' : 'entries'} for <strong>{submitDateInfo.date}</strong>?
                  </>
                )}
                <br />
                <span className="text-[#DC2626] font-medium mt-2 block">This will lock them and prevent further editing.</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmSubmitTimesheet}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
                <button
                  onClick={() => setShowSubmitConfirmModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-[#E4E4E7] text-[#52525B] text-sm font-medium hover:bg-[#F4F4F5] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Entries Display */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="p-4 border-b border-[#E4E4E7]">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A]">
                Timesheet Entries
              </h2>
              <div className="flex items-center gap-4">
                <div className="text-sm text-[#52525B]">
                  <span>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
                  {entries.length > 0 && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="font-bold text-[#09090B]">{totalHoursFormatted} total</span>
                    </>
                  )}
                </div>
                
                {/* Unlock button for single date mode (Admin/Super Admin) */}
                {!isRangeMode && (user.role === 'admin' || user.role === 'super_admin') && entries.some(e => e.submitted) && (
                  <button
                    onClick={() => setUnlockingDate(selectedDate)}
                    className="px-3 py-1.5 bg-[#DC2626] text-white text-xs font-medium hover:bg-[#B91C1C] transition-colors flex items-center gap-2 border border-[#B91C1C]"
                  >
                    <Unlock className="w-3 h-3" strokeWidth={2} />
                    Unlock {selectedDate}
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#71717A]">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center text-[#71717A]">
              No entries found for selected date{isRangeMode ? ' range' : ''}
            </div>
          ) : isRangeMode ? (
            // Unified table for range mode with consistent columns across all dates
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7] sticky top-0">
                  <tr>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '110px' }}>Time</th>
                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '80px' }}>Duration</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '200px' }}>Activity</th>
                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '60px' }}>Type</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '140px' }}>Client/Lead</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Notes</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '100px' }}>Status</th>
                    {user.role === 'poc' && (
                      <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]" style={{ width: '80px' }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map(date => {
                    const dayEntries = groupedByDate[date];
                    // Filter: Admins only see submitted entries, POCs see all
                    let filteredEntries = (user.role === 'admin' || user.role === 'super_admin') 
                      ? dayEntries.filter(e => e.submitted) 
                      : dayEntries;
                    
                    // Apply activity and type filters for admin/super_admin
                    if ((user.role === 'admin' || user.role === 'super_admin')) {
                      if (filterActivity) {
                        filteredEntries = filteredEntries.filter(e => e.activity_type === filterActivity);
                      }
                      if (filterType) {
                        filteredEntries = filteredEntries.filter(e => e.rga_nra === filterType);
                      }
                    }
                    
                    // Skip dates with no visible entries for this role
                    if (filteredEntries.length === 0) return null;
                    
                    const isExpanded = expandedDays[date];
                    const dayTotalHours = filteredEntries.reduce((sum, e) => sum + e.duration_hours, 0);
                    const rgaTotalHours = filteredEntries.filter(e => e.rga_nra === 'RGA').reduce((sum, e) => sum + e.duration_hours, 0);
                    const nraTotalHours = filteredEntries.filter(e => e.rga_nra === 'NRA').reduce((sum, e) => sum + e.duration_hours, 0);
                    const dayIsSubmitted = filteredEntries.every(e => e.submitted);
                    const colSpan = user.role === 'poc' ? 8 : 7;
                    
                    return (
                      <React.Fragment key={date}>
                        {/* Date Section Header Row */}
                        <tr className="bg-[#FAFAFA] hover:bg-[#F4F4F5] border-t-2 border-[#E4E4E7]">
                          <td colSpan={colSpan} className="p-0">
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => toggleDayExpansion(date)}
                                className="flex-1 p-4 flex items-center gap-3 text-left"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-[#52525B]" strokeWidth={2} />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-[#52525B]" strokeWidth={2} />
                                )}
                                <span className="text-sm font-bold text-[#09090B]">
                                  {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                <span className="text-xs text-[#71717A]">
                                  {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
                                </span>
                                <span className="text-sm font-bold text-[#09090B]">
                                  {formatDuration(dayTotalHours)} total
                                </span>
                                {(rgaTotalHours > 0 || nraTotalHours > 0) && (
                                  <>
                                    <span className="text-xs text-[#71717A]">•</span>
                                    {rgaTotalHours > 0 && (
                                      <span className="text-xs text-green-700 font-medium">
                                        RGA: {formatDuration(rgaTotalHours)}
                                      </span>
                                    )}
                                    {nraTotalHours > 0 && (
                                      <span className="text-xs text-gray-700 font-medium">
                                        NRA: {formatDuration(nraTotalHours)}
                                      </span>
                                    )}
                                  </>
                                )}
                              </button>
                              
                              {/* Admin Unlock Button */}
                              {(user.role === 'admin' || user.role === 'super_admin') && filteredEntries.some(e => e.submitted) && (
                                <button
                                  onClick={() => {
                                    setUnlockingDate(date);
                                    setUnlockingUserEmail(selectedPOC);
                                    setShowUnlockModal(true);
                                  }}
                                  className="mr-4 px-3 py-1 bg-[#DC2626] text-white text-xs font-medium hover:bg-[#b91c1c] transition-colors flex items-center gap-1"
                                >
                                  <Unlock className="w-3 h-3" strokeWidth={2} />
                                  Unlock
                                </button>
                              )}
                              
                              {/* POC Submit Day Button */}
                              {user.role === 'poc' && !dayIsSubmitted && (
                                <button
                                  onClick={() => handleSubmitSpecificDate(date)}
                                  className="mr-4 px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors"
                                >
                                  Submit Day
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        
                        {/* Entry Rows for this Date */}
                        {isExpanded && filteredEntries.map((entry, idx) => {
                          const isNoteExpanded = expandedNote === entry.entry_id;
                          const noteIsLong = entry.notes && entry.notes.length > 50;
                          
                          return (
                            <tr key={entry.entry_id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                              <td className="p-3 text-sm text-[#52525B] font-mono" style={{ whiteSpace: 'nowrap' }}>
                                {entry.start_time} - {entry.end_time}
                              </td>
                              <td className="p-3 text-sm text-[#09090B] font-bold text-center">
                                {formatDuration(entry.duration_hours)}
                              </td>
                              <td className="p-3 text-sm text-[#52525B]">
                                {entry.activity_type || '—'}
                              </td>
                              <td className="p-3 text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                                  entry.rga_nra === 'RGA' 
                                    ? 'bg-green-100 text-green-900 border border-green-900'
                                    : 'bg-gray-100 text-gray-900 border border-gray-900'
                                }`}>
                                  {entry.rga_nra}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-[#52525B]">
                                {entry.firm || '—'}
                              </td>
                              <td 
                                className="p-3 text-sm text-[#52525B] cursor-pointer hover:bg-[#F4F4F5]"
                                onClick={() => noteIsLong && toggleNoteExpansion(entry.entry_id)}
                                style={{ maxWidth: '300px' }}
                              >
                                <div className="flex items-start gap-1">
                                  <span className={isNoteExpanded ? '' : 'line-clamp-1'} style={!isNoteExpanded ? { overflow: 'hidden', textOverflow: 'ellipsis' } : {}}>
                                    {entry.notes}
                                  </span>
                                  {noteIsLong && (
                                    <span className="text-[#71717A] flex-shrink-0">
                                      {isNoteExpanded ? '▲' : '▼'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                                  entry.submitted
                                    ? 'bg-blue-100 text-blue-900 border border-blue-900'
                                    : 'bg-yellow-100 text-yellow-900 border border-yellow-900'
                                }`}>
                                  {entry.submitted ? 'Submitted' : 'Draft'}
                                </span>
                              </td>
                              {user.role === 'poc' && (
                                <td className="p-3">
                                  {!entry.submitted && (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleEdit(entry)}
                                        className="p-1 text-[#0C9168] hover:bg-[#F4F4F5] transition-colors"
                                        title="Edit"
                                      >
                                        <Edit2 className="w-4 h-4" strokeWidth={2} />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(entry.entry_id)}
                                        className="p-1 text-[#DC2626] hover:bg-[#F4F4F5] transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            // Standard table view (single date mode - NO DATE COLUMN)
            <div className="overflow-x-auto">
              <table className="w-full" style={{ tableLayout: 'auto' }}>
                <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                  <tr>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Time</th>
                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider text-[#71717A]">Duration</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Activity</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Type</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Client/Lead</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Notes</th>
                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Status</th>
                    {user.role === 'poc' && (
                      <th className="p-3 text-left text-xs font-bold uppercase tracking-wider text-[#71717A]">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {entries
                    .filter(entry => {
                      // Apply activity and type filters for admin/super_admin
                      if ((user.role === 'admin' || user.role === 'super_admin')) {
                        if (filterActivity && entry.activity_type !== filterActivity) return false;
                        if (filterType && entry.rga_nra !== filterType) return false;
                      }
                      return true;
                    })
                    .map((entry, idx) => {
                    const isNoteExpanded = expandedNote === entry.entry_id;
                    const noteIsLong = entry.notes && entry.notes.length > 50;
                    
                    return (
                      <tr key={idx} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                        <td className="p-3 text-sm text-[#52525B] font-mono">
                          {entry.start_time} - {entry.end_time}
                        </td>
                        <td className="p-3 text-sm text-[#09090B] font-bold text-center">{formatDuration(entry.duration_hours)}</td>
                        <td className="p-3 text-sm text-[#52525B]">
                          {entry.activity_type || '—'}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                            entry.rga_nra === 'RGA' 
                              ? 'bg-green-100 text-green-900 border border-green-900'
                              : entry.rga_nra === 'NRA'
                              ? 'bg-gray-100 text-gray-900 border border-gray-900'
                              : ''
                          }`}>
                            {entry.rga_nra || '—'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-[#52525B]">
                          {entry.firm || '—'}
                        </td>
                        <td 
                          className="p-3 text-sm text-[#52525B] cursor-pointer hover:bg-[#F4F4F5]"
                          onClick={() => noteIsLong && toggleNoteExpansion(entry.entry_id)}
                        >
                          <div className="flex items-start gap-1">
                            <span className={isNoteExpanded ? '' : 'line-clamp-1'}>
                              {entry.notes}
                            </span>
                            {noteIsLong && (
                              <span className="text-[#71717A] flex-shrink-0">
                                {isNoteExpanded ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase ${
                            entry.submitted
                              ? 'bg-blue-100 text-blue-900 border border-blue-900'
                              : 'bg-yellow-100 text-yellow-900 border border-yellow-900'
                          }`}>
                            {entry.submitted ? 'Submitted' : 'Draft'}
                          </span>
                        </td>
                        {user.role === 'poc' && (
                          <td className="p-3">
                            {!entry.submitted && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEdit(entry)}
                                  className="p-1 text-[#0C9168] hover:bg-[#F4F4F5] transition-colors"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" strokeWidth={2} />
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.entry_id)}
                                  className="p-1 text-[#DC2626] hover:bg-[#F4F4F5] transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Submit Button Section for POCs */}
          {user.role === 'poc' && !isRangeMode && canSubmitDate() && (
            <div className="p-4 border-t border-[#E4E4E7] bg-[#FAFAFA]">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#71717A]">
                  You have unsubmitted entries for <strong>{selectedDate}</strong>
                </p>
                <button
                  onClick={handleSubmitTimesheet}
                  className="px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors"
                >
                  Submit Day
                </button>
              </div>
            </div>
          )}

          {/* Submit All Button for POC Range Mode */}
          {user.role === 'poc' && isRangeMode && entries.some(e => !e.submitted) && (
            <div className="p-4 border-t border-[#E4E4E7] bg-[#FAFAFA]">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#71717A]">
                  You have unsubmitted entries across multiple dates
                </p>
                <button
                  onClick={handleSubmitAllUnsubmitted}
                  className="px-4 py-2 bg-[#0C9168] text-white text-sm font-medium hover:bg-[#0a7a58] transition-colors"
                >
                  Submit All Unsubmitted
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
